import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { ErrorModal } from '../components/ErrorModal';

export interface ErrorState {
  message: string;
  title?: string;
}

export interface ErrorContextValue {
  error: ErrorState | null;
  showError: (message: string, title?: string) => void;
  clearError: () => void;
}

export const ErrorContext = createContext<ErrorContextValue | null>(null);

function sanitizeMessage(message: string): string {
  // eslint-disable-next-line no-secrets/no-secrets -- false positive: React prop name, not a secret
  // Strip HTML tags to prevent XSS — never render error messages via dangerouslySetInnerHTML
  return message.replace(/<[^>]*>/g, '');
}

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [error, setError] = useState<ErrorState | null>(null);

  const showError = useCallback((message: string, title?: string) => {
    const sanitizedMessage = sanitizeMessage(message);
    const sanitizedTitle = title !== undefined ? sanitizeMessage(title) : undefined;
    setError({ message: sanitizedMessage, title: sanitizedTitle });
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Expose showError on window.__showError for E2E testing
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__showError = showError;
    return () => {
      delete (window as unknown as Record<string, unknown>).__showError;
    };
  }, [showError]);

  return (
    <ErrorContext.Provider value={{ error, showError, clearError }}>
      {children}
      {error !== null && (
        <ErrorModal
          message={error.message}
          title={error.title}
          onClose={clearError}
        />
      )}
    </ErrorContext.Provider>
  );
}

/**
 * Hook for triggering and clearing the global error modal.
 * Use this hook in components and API call handlers to display system/API errors.
 *
 * NOTE: This modal is for system/API errors only — form field validation errors
 * should remain inline next to the relevant form fields, not use this modal.
 *
 * @example
 * const { showError } = useError();
 * try {
 *   await apiCall();
 * } catch (err) {
 *   showError(err instanceof Error ? err.message : 'An unexpected error occurred');
 * }
 */
export function useError(): ErrorContextValue {
  const ctx = useContext(ErrorContext);
  if (ctx === null) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return ctx;
}
